import { useCatalogStore } from '@/stores/catalog'
import { useCommerceStore } from '@/stores/commerce'
import { defineStore } from 'pinia'

import constants from '@/lib/constants'

import { CadyMarket } from '@/types/CadyMarket'
import { CartItemCustomization, CustomizationType, PromptType } from '@/types/CartItemCustomization'
import { CartItemSelection } from '@/types/CartItemSelection'
import type { LocalCartItemCustomization } from '@/types/LocalCartItemCustomization'

import {
  CartBundleStorefrontDto,
  CartBundleUpdateStorefrontDto,
  CartItemCustomizationDto,
  CartItemImageCustomizationDto,
  MediaReferenceDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

interface CartConfigurationStore {
  itemCustomizations: CartItemCustomization[]
  itemSelections: CartItemSelection[]
  /**
   * Temporary draft values for customizations being edited in a context.
   * These values are copied from itemCustomizations when a context opens,
   * modified during editing, then either committed back or discarded.
   * Intentionally excluded from persistence as it's transient editing state.
   */
  draftPromptValues: LocalCartItemCustomization[]
}

export const useCartConfigurationStore = defineStore('cartConfiguration', {
  state: (): CartConfigurationStore => ({
    // All state properties should be added to the manual resetStore action method below
    itemCustomizations: [],
    itemSelections: [],
    draftPromptValues: []
  }),
  getters: {
    itemsCustomizationsByCartBundleId(
      state: CartConfigurationStore
    ): (cartBundleId: string) => CartItemCustomization[] {
      return (cartBundleId: string): CartItemCustomization[] => {
        return state.itemCustomizations.filter(
          (itemCustomization) => itemCustomization.cartBundleId === cartBundleId
        )
      }
    },
    itemsCustomizationsByCartItemId(
      state: CartConfigurationStore
    ): (cartItemId: string) => CartItemCustomization[] {
      return (cartItemId: string): CartItemCustomization[] => {
        return state.itemCustomizations.filter((itemCustomization) =>
          itemCustomization.itemHasCartItemIds([cartItemId])
        )
      }
    },
    customizationsAsCartItemCustomizationDtos(
      state: CartConfigurationStore
    ): (market: CadyMarket) => (CartItemCustomizationDto | CartItemImageCustomizationDto)[] {
      return (market: CadyMarket) => {
        // transform grouped pose selections back to CartItemCustomizationDto here
        let cartItemCustomizationDtos: (
          | CartItemCustomizationDto
          | CartItemImageCustomizationDto
        )[] = []

        // For each itemCustomization, we need to check if it has a singleItemCustomization or multiItemCustomization
        state.itemCustomizations.forEach((itemCustomization) => {
          cartItemCustomizationDtos = cartItemCustomizationDtos.concat(
            itemCustomization.toCartItemCustomizationDtos(market)
          )
        })

        return cartItemCustomizationDtos
      }
    },
    getImagePromptValues(
      state: CartConfigurationStore
    ): (cartItemIds: string[], promptId: string) => MediaReferenceDto[] {
      return (cartItemIds: string[], promptId: string): MediaReferenceDto[] => {
        const itemCustomization = state.itemCustomizations.find((ic) =>
          ic.itemHasCartItemIdsAndPromptId(cartItemIds, promptId)
        )

        if (itemCustomization) {
          if (itemCustomization.singleItemCustomization != null) {
            const promptValuePair = itemCustomization.singleItemCustomization.promptValuePairs.find(
              (pair) => pair.promptId === promptId
            )

            if (promptValuePair && promptValuePair.value) {
              return [promptValuePair.value as MediaReferenceDto]
            }
          } else if (itemCustomization.multiItemCustomization != null) {
            return itemCustomization.multiItemCustomization.cartItemValuePairs
              .filter((pair) => cartItemIds.includes(pair.cartItemId) && pair.value)
              .map((pair) => pair.value as MediaReferenceDto)
          }
        }

        return []
      }
    },
    customizationValidationValues(): Record<string, string> {
      // This generates an object with a key of itemCustomizationId and a value of 'valid' or null
      // depending on if the item customization has all value pairs filled
      // This is used for validation purposes within components

      const result = {}

      this.itemCustomizations.forEach((ic: CartItemCustomization) => {
        result[ic.itemCustomizationId] = ic.allRequiredValuePairsHaveAValue() ? 'valid' : null
      })

      return result
    },
    itemSelectionsByCartBundleId(
      state: CartConfigurationStore
    ): (cartBundleId: string) => CartItemSelection[] {
      return (cartBundleId: string): CartItemSelection[] => {
        return state.itemSelections.filter(
          (itemSelection) => itemSelection.cartBundleId === cartBundleId
        )
      }
    },
    /**
     * Get a prompt value by promptId and cartItemId.
     * Searches draftPromptValues first, then falls back to itemCustomizations.
     */
    getPromptValue(
      state: CartConfigurationStore
    ): (promptId: string, cartItemId: string) => string | MediaReferenceDto | undefined {
      return (promptId: string, cartItemId: string): string | MediaReferenceDto | undefined => {
        // Search draftPromptValues first
        const draftValue = state.draftPromptValues.find(
          (pv) => pv.cartItemIds.includes(cartItemId) && pv.promptId === promptId
        )
        if (draftValue) {
          return draftValue.value
        }

        // Fall back to itemCustomizations
        const configItem = state.itemCustomizations.find((ic) =>
          ic.itemHasCartItemIdsAndPromptId([cartItemId], promptId)
        )

        if (!configItem) {
          return undefined
        }

        return configItem.getPromptValueByIdealPairId(promptId, cartItemId) ?? undefined
      }
    },
    /**
     * Get prompt values as an array by promptId and cartItemId.
     * Searches draftPromptValues first, then falls back to itemCustomizations.
     */
    getPromptValues(
      state: CartConfigurationStore
    ): (promptId: string, cartItemId: string) => (string | MediaReferenceDto)[] {
      return (promptId: string, cartItemId: string): (string | MediaReferenceDto)[] => {
        // Search draftPromptValues first
        const draftValue = state.draftPromptValues.find(
          (pv) => pv.cartItemIds.includes(cartItemId) && pv.promptId === promptId
        )
        if (draftValue) {
          return [draftValue.value]
        }

        // Fall back to itemCustomizations
        const configItem = state.itemCustomizations.find((ic) =>
          ic.itemHasCartItemIdsAndPromptId([cartItemId], promptId)
        )

        if (!configItem) {
          return []
        }

        const value = configItem.getPromptValueByIdealPairId(promptId, cartItemId)
        return value ? [value] : []
      }
    }
  },
  actions: {
    /**
     * Rebuilds `itemCustomizations` and `itemSelections` from the cart, so the editing state
     * reflects what the cart actually holds. Called on entry to every configuration surface and
     * immediately before the sweeps that write customizations back, which is also what drops
     * entries for bundles that have since left the cart — the cart store used to prune those on
     * removal, but every consumer regenerates before it reads, so a stale entry never survives
     * to a read. Mirrors `packageBuilderStore.hydrateFromCart`.
     */
    hydrateFromCart(): void {
      const commerceStore = useCommerceStore()
      this.itemCustomizations = commerceStore.generateCartItemCustomizations()
      this.itemSelections = commerceStore.generateCartItemSelections()
    },
    updateItemCustomization(itemCustomization: CartItemCustomization): void {
      // Find the item customization to update
      let index = -1
      if (itemCustomization.singleItemCustomization != null) {
        index = this.itemCustomizations.findIndex((ic) => {
          return (
            ic.singleItemCustomization?.cartItemId ===
            itemCustomization.singleItemCustomization?.cartItemId
          )
        })
      } else {
        // Find the index of the item customization to update
        index = this.itemCustomizations.findIndex((ic) => {
          return ic.multiItemCustomization?.cartItemValuePairs.every((civp) =>
            itemCustomization.multiItemCustomization?.someCartItemValuePairsHaveCartItemId(
              civp.cartItemId
            )
          )
        })
      }

      if (index === -1) {
        throw new Error('Could not find item customization to update')
      }

      this.itemCustomizations[index] = itemCustomization
    },
    // NOTE: This an internal method used by updatePromptValue. Use updatePromptValue in component implementations, not this method itself.
    updateSingleItemPromptValue(
      cartBundleId: string,
      cartItemId: string,
      promptId: string,
      value: string | MediaReferenceDto
    ): void {
      // Find existing cart item customization
      const cartItemCustomization = this.itemCustomizations.find((itemCustomization) =>
        itemCustomization.itemHasCartItemIds([cartItemId])
      )

      if (cartItemCustomization) {
        // Get the prompt value pair by promptId
        const promptValuePair = cartItemCustomization.getValuePairByPairId(promptId)

        // If the prompt value pair exists, update it
        // Otherwise, create a new one and add it to the promptValuePairs array
        if (promptValuePair) {
          promptValuePair.value = value
        } else {
          throw new Error('Could not find prompt value pair to update')
        }

        // Create a new ItemCustomization object with the updated singleItemCustomization
        const updatedCartItemCustomization = new CartItemCustomization(
          cartItemCustomization.itemCustomizationId, // give this the same id as the one being replaced
          cartBundleId,
          null,
          cartItemCustomization.singleItemCustomization ?? null
        )

        // Update the item customization in the store
        this.updateItemCustomization(updatedCartItemCustomization)
      } else {
        throw new Error('Could not find item customization to update')
      }
    },
    // NOTE: This an internal method used by updatePromptValue. Use updatePromptValue in component implementations, not this method itself.
    upsertMultiItemPromptValue(
      cartItemId: string,
      promptId: string,
      value: string | MediaReferenceDto | null
    ): void {
      // Find existing cart item customization
      const cartItemCustomization = this.itemCustomizations.find((itemCustomization) =>
        itemCustomization.itemHasCartItemIdsAndPromptId([cartItemId], promptId)
      )

      // If the cart item customization exists, update it
      if (cartItemCustomization) {
        const cartItemValuePair = cartItemCustomization.getValuePairByPairId(cartItemId)

        // If the cart item value pair exists, update it
        // Otherwise, create a new one and add it to the cartItemValuePairs array
        if (cartItemValuePair) {
          cartItemValuePair.value = value
        }

        // Create a new ItemCustomization object with the updated multiItemCustomization
        const updatedCartItemCustomization = new CartItemCustomization(
          cartItemCustomization.itemCustomizationId, // give this the same id as the one being replaced
          cartItemCustomization.cartBundleId,
          cartItemCustomization.multiItemCustomization,
          null
        )

        // Update the item customization in the store
        this.updateItemCustomization(updatedCartItemCustomization)
      } else {
        throw new Error('Could not find item customization to update')
      }
    },
    updatePromptValue(
      cartItems: string[],
      promptId: string,
      value: string | MediaReferenceDto
    ): boolean {
      const existingCustomization = this.itemCustomizations.find((ic: CartItemCustomization) =>
        ic.itemHasCartItemIdsAndPromptId(cartItems, promptId)
      )

      if (!existingCustomization) {
        throw new Error(`No existing customization found for prompt: ${promptId}`)
      }

      // If the existing customization is of type SINGLE_ITEM, update the value
      if (existingCustomization.isSingleItemCustomization()) {
        this.updateSingleItemPromptValue(
          existingCustomization.cartBundleId,
          cartItems[0],
          promptId,
          value
        )
      }
      // If the existing customization is of type MULTI_ITEM and is not a decrease action, find the first null cartItemValuePair
      else if (existingCustomization.type === CustomizationType.MULTI_ITEM) {
        // Find first null cartItemValuePair
        const cartItemValuePair =
          existingCustomization.multiItemCustomization?.cartItemValuePairs.find(
            (cartItemValuePair) => cartItemValuePair.value === null
          )

        if (!cartItemValuePair) {
          // If no null cartItemValuePair is found, return false to indicate that the update was not successful
          return false
        }

        // Update the value of the cartItemValuePair
        this.upsertMultiItemPromptValue(cartItemValuePair.cartItemId, promptId, value)
      }

      return true
    },
    removePoseFromImagePrompt(cartItemIds: string[], promptId: string, poseId: string): void {
      // find the item customization related to the cartItems
      const itemCustomization = this.itemCustomizations.find((ic) =>
        ic.itemHasCartItemIdsAndPromptId(cartItemIds, promptId)
      )

      if (!itemCustomization) {
        throw new Error('Could not find item customization to remove pose from')
      }

      if (itemCustomization.singleItemCustomization) {
        const promptValuePair = itemCustomization.singleItemCustomization.promptValuePairs.find(
          (pair) =>
            pair.promptId === promptId && (pair.value as MediaReferenceDto)?.canonicalUrl === poseId
        )

        if (promptValuePair && promptValuePair.promptType === PromptType.IMAGE) {
          promptValuePair.value = null
        } else if (promptValuePair && promptValuePair.promptType !== PromptType.IMAGE) {
          throw new Error('Cannot remove pose from non image prompt')
        } else {
          throw new Error('Could not find prompt value pair to remove pose from')
        }
      } else if (itemCustomization.multiItemCustomization) {
        const cartItemValuePair = itemCustomization.multiItemCustomization.cartItemValuePairs.find(
          (pair) =>
            cartItemIds.includes(pair.cartItemId) &&
            (pair.value as MediaReferenceDto)?.canonicalUrl === poseId
        )

        if (cartItemValuePair && cartItemValuePair.promptType === PromptType.IMAGE) {
          cartItemValuePair.value = null
        } else if (cartItemValuePair && cartItemValuePair.promptType !== PromptType.IMAGE) {
          throw new Error('Cannot remove pose from non image prompt')
        } else {
          throw new Error('Could not find cart item value pair to remove pose from')
        }
      }
    },
    /**
     * Overlays unsaved values from an earlier `itemCustomizations` / `itemSelections` pair onto the
     * current, regenerated ones. Used after a failed configure save: the cart is refreshed and the
     * page regenerated from it, and this puts the customer's entries back for every cart item and
     * item group that still exists. Values are written into the current objects in place, so
     * anything holding a reference to them (`CartConfigData`) sees the restored values.
     */
    restoreDraft(
      previousCustomizations: CartItemCustomization[],
      previousSelections: CartItemSelection[],
      currentCartBundleIds: string[]
    ): void {
      this.itemCustomizations.forEach((current) => {
        const cartItemIds = current.getCartItemIds()

        if (current.singleItemCustomization) {
          const previous = previousCustomizations.find(
            (pc) => pc.isSingleItemCustomization() && pc.itemHasCartItemIds(cartItemIds)
          )
          if (!previous) return

          current.singleItemCustomization.promptValuePairs.forEach((pair) => {
            const previousValue = previous.getValueByPairId(pair.promptId)
            if (previousValue !== null) pair.value = previousValue
          })
        } else if (current.multiItemCustomization) {
          const promptId = current.multiItemCustomization.promptId
          const previous = previousCustomizations.find(
            (pc) =>
              pc.isMultiItemCustomization() &&
              pc.itemHasCartItemIdsAndPromptId(cartItemIds, promptId)
          )
          if (!previous) return

          current.multiItemCustomization.cartItemValuePairs.forEach((pair) => {
            const previousValue = previous.getValueByPairId(pair.cartItemId)
            if (previousValue !== null) pair.value = previousValue
          })
        }
      })

      previousSelections.forEach((previousSelection) => {
        if (
          !previousSelection.cartBundleId ||
          !previousSelection.itemGroupId ||
          !currentCartBundleIds.includes(previousSelection.cartBundleId)
        ) {
          return
        }

        this.upsertItemSelection(
          previousSelection.cartBundleId,
          previousSelection.itemGroupId,
          previousSelection.itemGroupItemId,
          previousSelection.itemId
        )
      })
    },
    resetStore(): void {
      // This method is used to reset the store to its initial state manually because of the use of persist
      // Any state property added above should be reset here
      this.itemCustomizations = []
      this.itemSelections = []
      this.draftPromptValues = []
    },
    /**
     * Initialize draft prompt values from existing customizations for a cart item.
     * Call this when opening a customization modal to populate the draft with current values.
     */
    initializeDraftForCartItem(
      cartItemId: string,
      cartBundleId: string,
      promptIds: string[]
    ): void {
      // Clear any existing draft values for this cart item
      this.draftPromptValues = this.draftPromptValues.filter(
        (pv) => !pv.cartItemIds.includes(cartItemId)
      )

      // Copy existing customization values to draft
      const existingCustomizations = this.itemsCustomizationsByCartBundleId(cartBundleId)

      promptIds.forEach((promptId) => {
        const customization = existingCustomizations.find((ic) =>
          ic.itemHasCartItemIdsAndPromptId([cartItemId], promptId)
        )

        if (customization) {
          const value = customization.getPromptValueByIdealPairId(promptId, cartItemId)
          if (value !== null && value !== undefined) {
            this.draftPromptValues.push({ cartItemIds: [cartItemId], promptId, value })
          }
        }
      })
    },
    /**
     * Update a draft prompt value. Creates a new entry if one doesn't exist.
     */
    updateDraftPromptValue(
      cartItemIds: string[],
      promptId: string,
      value: string | MediaReferenceDto
    ): void {
      const existingIndex = this.draftPromptValues.findIndex(
        (pv) => pv.cartItemIds.some((id) => cartItemIds.includes(id)) && pv.promptId === promptId
      )

      if (existingIndex !== -1) {
        this.draftPromptValues[existingIndex].value = value
      } else {
        this.draftPromptValues.push({ cartItemIds, promptId, value })
      }
    },
    /**
     * Commit draft prompt values to the actual itemCustomizations.
     * Call this when user saves their changes.
     */
    commitDraftForCartItem(cartItemIds: string[]): void {
      const draftValues = this.draftPromptValues.filter((pv) =>
        pv.cartItemIds.some((id) => cartItemIds.includes(id))
      )

      draftValues.forEach(({ cartItemIds: draftCartItemIds, promptId, value }) => {
        const customization = this.itemCustomizations.find((ic) =>
          ic.itemHasCartItemIdsAndPromptId(draftCartItemIds, promptId)
        )

        // A draft edits its own cart item's slot; `updatePromptValue` would fill the first empty one
        if (customization?.isMultiItemCustomization()) {
          draftCartItemIds.forEach((id) => this.upsertMultiItemPromptValue(id, promptId, value))
        } else {
          this.updatePromptValue(draftCartItemIds, promptId, value)
        }
      })

      // Clear draft for this cart item
      this.discardDraftForCartItem(cartItemIds)
    },
    /**
     * Discard draft prompt values without saving.
     * Call this when user cancels or closes the modal without saving.
     */
    discardDraftForCartItem(cartItemIds: string[]): void {
      this.draftPromptValues = this.draftPromptValues.filter(
        (pv) => !pv.cartItemIds.some((id) => cartItemIds.includes(id))
      )
    },
    handleUnderclassEmptyImagePrompts(mediaId: number, backgroundImageValue: string | null) {
      // If there are no cart item customizations, mediaId, or backgroundImageValue, return early
      if (
        !this.itemCustomizations ||
        this.itemCustomizations.length === 0 ||
        !mediaId ||
        !backgroundImageValue
      ) {
        return
      }

      // The Sunny16 composite thumbnail bakes the selected background into its
      // URL, so it must reflect the current background selection.
      const desiredThumbUrl = `${import.meta.env.VITE_SUNNY_16_RENDER_API_BASE_URL}composite-thumb/${mediaId}/${backgroundImageValue}`

      // Fill empty Underclass image prompts with the composite thumbnail, and
      // refresh any existing composite whose baked-in background is now stale
      // (e.g. the customer changed their background after a prior visit). UC
      // customers never pick a pose, so every image prompt here is an
      // auto-generated composite and is safe to re-point at the current one.
      this.itemCustomizations.forEach((itemCustomization) => {
        // TODO: We are only handling single item customizations here for now. If multi item customizations are added in the future, we will need to handle those as well.
        if (itemCustomization.singleItemCustomization) {
          itemCustomization.singleItemCustomization.promptValuePairs.forEach((promptValuePair) => {
            if (promptValuePair.promptType !== PromptType.IMAGE) {
              return
            }

            const existingThumbUrl = (promptValuePair.value as MediaReferenceDto)?.thumbUrl ?? null
            const isEmpty = promptValuePair.value === null || !existingThumbUrl
            const isStaleComposite =
              !!existingThumbUrl &&
              existingThumbUrl.includes('/composite-thumb/') &&
              existingThumbUrl !== desiredThumbUrl

            if (isEmpty || isStaleComposite) {
              const defaultValue = new MediaReferenceDto()
              // If the value is null, set the canonicalUrl to the Underclass image prompt placeholder
              defaultValue.canonicalUrl =
                (promptValuePair.value as MediaReferenceDto)?.canonicalUrl ??
                constants.underclassImagePromptPlaceholder
              // Set the thumbUrl to the Sunny16 composite thumbnail URL
              defaultValue.thumbUrl = desiredThumbUrl
              // Give the default value a top-crop
              // TODO: Make this less "magical" in the future
              defaultValue.attributes = 'x1=0&y1=0&x2=1&y2=0.8'
              promptValuePair.value = defaultValue
            }
          })
        }
      })
    },
    upsertItemSelection(
      cartBundleId: string | null,
      itemGroupId: string,
      selectedItemGroupItemId: string,
      selectedItemId: string
    ) {
      if (cartBundleId !== null) {
        // remove existing selection for cartBundleId and itemGroupId
        this.itemSelections = this.itemSelections.filter(
          (selection) =>
            !(selection.cartBundleId === cartBundleId && selection.itemGroupId === itemGroupId)
        )
      }

      // add new selection
      const newSelection = new CartItemSelection(
        cartBundleId,
        itemGroupId,
        selectedItemGroupItemId,
        selectedItemId
      )

      this.itemSelections.push(newSelection)
    },
    removeItemSelection(cartBundleId: string, itemGroupId: string) {
      this.itemSelections = this.itemSelections.filter(
        (selection) =>
          !(selection.cartBundleId === cartBundleId && selection.itemGroupId === itemGroupId)
      )
    },
    getCartBundleUpdateStorefrontDto(
      cartBundle: CartBundleStorefrontDto,
      cadyMarket: CadyMarket
    ): CartBundleUpdateStorefrontDto {
      const cartBundleUpdateDto: CartBundleUpdateStorefrontDto = new CartBundleUpdateStorefrontDto()

      cartBundleUpdateDto.cartBundleId = cartBundle.cartBundleId
      cartBundleUpdateDto.quantity = cartBundle.quantity

      const itemSelections = this.itemSelectionsByCartBundleId(cartBundle.cartBundleId)

      // The server deletes an optional cart item that is absent from `selectedItems` before it
      // applies customizations, so a customization for a deselected item would point at a row that
      // no longer exists. Drop those; keep customizations for included-group items, which the
      // server never deletes, and for items whose group is unknown.
      const selectedItemGroupItemIds = new Set(itemSelections.map((s) => s.itemGroupItemId))
      const optionalItemGroupItemIds = new Set(
        useCatalogStore()
          .catalogData?.catalogBundles?.find(
            (cb) => cb.catalogBundleId === cartBundle.catalogBundleId
          )
          ?.bundle?.itemGroups?.filter((ig) => !ig.isIncluded)
          .flatMap((ig) => ig.itemGroupItems?.map((igi) => igi.itemGroupItemId) ?? []) ?? []
      )
      const deselectedCartItemIds = new Set(
        cartBundle.cartItems
          .filter(
            (ci) =>
              ci.itemGroupItemId &&
              optionalItemGroupItemIds.has(ci.itemGroupItemId) &&
              !selectedItemGroupItemIds.has(ci.itemGroupItemId)
          )
          .map((ci) => ci.cartItemId)
      )

      const cartBundleItemCustomizations = this.itemsCustomizationsByCartBundleId(
        cartBundle.cartBundleId
      )

      if (cartBundleItemCustomizations.length > 0) {
        cartBundleUpdateDto.customizations = []

        cartBundleItemCustomizations.forEach((itemCustomization) => {
          cartBundleUpdateDto.customizations = cartBundleUpdateDto.customizations!.concat(
            itemCustomization
              .toCartItemCustomizationDtos(cadyMarket as CadyMarket)
              .filter((dto) => !deselectedCartItemIds.has(dto.cartItemId))
          )
        })
      }

      if (itemSelections.length > 0) {
        cartBundleUpdateDto.selectedItems = []

        itemSelections.forEach((itemSelection) => {
          cartBundleUpdateDto.selectedItems!.push(itemSelection.toCartItemUpdateStorefrontDto())
        })
      }

      return cartBundleUpdateDto
    }
  },
  persist: {
    // draftPromptValues is intentionally excluded as it's temporary editing state
    omit: ['draftPromptValues'],
    afterHydrate(ctx) {
      // Pinia persist serializes state to JSON, which strips class prototypes.
      // On rehydration, itemCustomizations and itemSelections are plain objects
      // missing their methods (e.g. itemHasCartItemIds, toCartItemCustomizationDtos).
      // Reconstitute them here to restore the full class instances.
      ctx.store.itemCustomizations = ctx.store.itemCustomizations.map((ic: CartItemCustomization) =>
        CartItemCustomization.fromPlainObject(ic)
      )
      ctx.store.itemSelections = ctx.store.itemSelections.map((is: CartItemSelection) =>
        CartItemSelection.fromPlainObject(is)
      )
    }
  }
})
